/**
 * Adapter - CBSClientAccountGateway
 *
 * Appelle le backend CardLife pour récupérer la liste des comptes et cartes
 * d'un client via le CBS.
 *
 * Endpoint : POST /api/v1/cbs-integration/clients/accounts
 *
 * Utilisé lors de la vérification d'identité pour afficher les comptes
 * associés à la carte insérée.
 */

import { buildBearerAuthHeader } from '../http/auth';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const CLIENT_ACCOUNTS_ENDPOINT = '/api/v1/cbs-integration/clients/accounts';
const CLIENT_PID_ENDPOINT = '/api/v1/cbs-integration/clients/pid';

// Response de l'API CBS
export interface CBSClientAccountsResponse {
  Liste_des_comptes: string; // Format: "compte1 ; devise ; type ; ..."
  Liste_des_carte: string;   // Format: "carte1,carte2,..."
  response: string;          // "0" = succès
  message: string;           // "succès" ou message d'erreur
  codeuser: string;          // Code de l'agent
}

// Réponse brute du CBS pour la pièce d'identité
export interface CBSClientPidResponse {
  Num_PID?: string;
  Type_PID?: string;
  Pays_Emission?: string;
  Delivre_Le?: string;     // Format DD/MM/YY
  Fin_Validite?: string;   // Format DD/MM/YY
  response?: string;       // "0" = succès
  message?: string;
}

// PID parsée (mappée vers le format frontend)
export interface ParsedPid {
  numero: string;          // Num_PID
  type: string;            // CNI | PASSPORT | DRIVER_LICENSE | CONSULAR_CARD
  paysEmission: string;
  dateDelivrance: string;  // ISO YYYY-MM-DD
  dateExpiration: string;  // ISO YYYY-MM-DD
}

/**
 * Convertit une date CBS "DD/MM/YY" en ISO "YYYY-MM-DD".
 * Hypothèse : YY < 100 → 2000 + YY (les pièces sont récentes).
 */
function parseCBSDate(ddMMyy: string | undefined): string {
  if (!ddMMyy) return '';
  const parts = ddMMyy.trim().split('/');
  if (parts.length !== 3) return '';
  const [dd, mm, yy] = parts;
  const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
  return `${year.toString().padStart(4, '0')}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

/**
 * Mappe le libellé CBS du type de pièce vers la valeur de notre enum frontend.
 */
function mapCBSPidType(typePID: string | undefined): string {
  if (!typePID) return 'CNI';
  const t = typePID.toLowerCase();
  if (t.includes('passeport') || t.includes('passport')) return 'PASSPORT';
  if (t.includes('permis') || t.includes('driver')) return 'DRIVER_LICENSE';
  if (t.includes('consulaire') || t.includes('consular')) return 'CONSULAR_CARD';
  return 'CNI';
}

// Compte parsé (plus facile à utiliser)
export interface ParsedAccount {
  accountNumber: string;
  currency: string;
  type: string;
  status: string;
  holderName: string;
  branchCode: string;
  branchName: string;
}

/**
 * Parse la chaîne Liste_des_comptes retournée par le CBS
 * Format: "15005000000000001 ; XOF;T ; LES ; A ; NOM PRENOMS TITULAIRE ; 15;AGENCE ; 15005;Plateau"
 *
 * Structure:
 * - [0] Numéro de compte
 * - [1] Devise
 * - [2] Type (T, E, etc.)
 * - [3] ??? (LES)
 * - [4] Statut (A = Actif)
 * - [5] Nom du/des titulaire(s)
 * - [6] Code agence;Nom agence
 * - [7] Code/Nom (autre info)
 */
function parseAccountsList(listeComptes: string): ParsedAccount[] {
  if (!listeComptes || listeComptes.trim() === '') {
    return [];
  }

  // Les comptes peuvent être séparés par des sauts de ligne ou des ;
  const accounts: ParsedAccount[] = [];

  // Séparer par ; et regrouper par blocs de données
  const parts = listeComptes.split(';').map(p => p.trim()).filter(p => p);

  // Heuristique: un numéro de compte est >= 13 chiffres
  let currentAccount: string[] = [];

  for (const part of parts) {
    // Si c'est un numéro de compte (13+ chiffres)
    if (/^\d{13,}$/.test(part)) {
      // Sauvegarder le compte précédent si on en a un
      if (currentAccount.length > 0) {
        accounts.push(parseAccountParts(currentAccount));
      }
      // Démarrer un nouveau compte
      currentAccount = [part];
    } else {
      // Ajouter au compte courant
      currentAccount.push(part);
    }
  }

  // Ne pas oublier le dernier compte
  if (currentAccount.length > 0) {
    accounts.push(parseAccountParts(currentAccount));
  }

  return accounts;
}

/**
 * Parse les parties d'un compte individuel
 */
function parseAccountParts(parts: string[]): ParsedAccount {
  return {
    accountNumber: parts[0] ?? '',
    currency: parts[1] ?? 'XOF',
    type: parts[2] ?? 'T',
    status: parts[4] ?? 'A',
    holderName: parts[5] ?? '',
    branchCode: parts[6]?.split(' ')[0] ?? '',
    branchName: parts[6]?.split(' ')[1] ?? '',
  };
}

/**
 * Parse la liste des cartes
 * Format: "5000500000000001" ou "carte1,carte2,carte3"
 */
function parseCardsList(listeCartes: string): string[] {
  if (!listeCartes || listeCartes.trim() === '') {
    return [];
  }

  // Séparer par virgule ou point-virgule
  return listeCartes
    .split(/[,;]/)
    .map(c => c.trim())
    .filter(c => c.length > 0);
}

export class CBSClientAccountGateway {
  /**
   * Récupère la liste des comptes et cartes associés à une carte
   *
   * @param cardNumber - Numéro de carte (ex: "5000500000000001")
   * @param codeuser - Code agent/caissier (obligatoire)
   * @returns Informations des comptes et cartes
   */
  async getClientAccounts(
    cardNumber: string,
    codeuser: string
  ): Promise<{
    raw: CBSClientAccountsResponse;
    accounts: ParsedAccount[];
    cards: string[];
  }> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${CLIENT_ACCOUNTS_ENDPOINT}`;

    if (!codeuser || codeuser.trim() === '') {
      throw new Error('Code agent (codeuser) obligatoire pour cette opération');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify({
          codeuser: codeuser.trim(),
          num_carte: cardNumber.trim(),
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[CBSClientAccount] Erreur HTTP:', response.status, text);
        throw new Error(`Erreur CBS: ${response.status} - ${text}`);
      }

      const data: CBSClientAccountsResponse = await response.json();

      // Vérifier le code réponse CBS
      if (data.response !== '0') {
        console.warn('[CBSClientAccount] Erreur CBS:', data.message);
        throw new Error(data.message || 'Erreur lors de la récupération des comptes');
      }

      // Parser les données
      const accounts = parseAccountsList(data.Liste_des_comptes);
      const cards = parseCardsList(data.Liste_des_carte);

      console.log('[CBSClientAccount] Comptes trouvés:', accounts.length);
      console.log('[CBSClientAccount] Cartes trouvées:', cards.length);

      return {
        raw: data,
        accounts,
        cards,
      };
    } catch (error) {
      console.error('[CBSClientAccount] Erreur lors de l\'appel:', error);
      throw error;
    }
  }

  /**
   * Récupère la pièce d'identification du client depuis le CBS à partir
   * du numéro de carte complet.
   *
   * @param cardNumber - Numéro de carte complet (ex: "5000500000000001")
   * @returns Données PID parsées (numéro, type, dates ISO, pays)
   */
  async getClientPid(cardNumber: string): Promise<{
    raw: CBSClientPidResponse;
    pid: ParsedPid;
  }> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${CLIENT_PID_ENDPOINT}`;

    if (!cardNumber || cardNumber.trim() === '') {
      throw new Error('Numéro de carte requis pour récupérer la pièce d\'identité');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify({ id: cardNumber.trim() }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[CBSClientPid] Erreur HTTP:', response.status, text);
        throw new Error(`Erreur CBS PID: ${response.status} - ${text}`);
      }

      // Le backend renvoie le JSON brut du CBS sous forme de String;
      // selon la sérialisation, on peut recevoir un objet ou une string JSON.
      const contentType = response.headers.get('content-type') ?? '';
      const raw = contentType.includes('application/json')
        ? await response.json()
        : JSON.parse(await response.text());

      const data: CBSClientPidResponse = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (data.response !== undefined && data.response !== '0') {
        console.warn('[CBSClientPid] Erreur CBS:', data.message);
        throw new Error(data.message || 'Erreur lors de la récupération de la pièce d\'identité');
      }

      const pid: ParsedPid = {
        numero: data.Num_PID ?? '',
        type: mapCBSPidType(data.Type_PID),
        paysEmission: data.Pays_Emission ?? '',
        dateDelivrance: parseCBSDate(data.Delivre_Le),
        dateExpiration: parseCBSDate(data.Fin_Validite),
      };

      console.log('[CBSClientPid] PID récupérée:', pid.numero, pid.type);

      return { raw: data, pid };
    } catch (error) {
      console.error('[CBSClientPid] Erreur lors de l\'appel:', error);
      throw error;
    }
  }
}
